import React, { useState } from 'react';
import { DATE_TIME_FORMAT, HEALTH_STATES } from '@/const';
import { format } from 'date-fns';
import { NotificationAlert } from '@/data/notifications';
import {
  useClearNotificationsMutation,
  useLazyGetNotificationsQuery,
  useReadNotificationsMutation,
} from '@/store/api/notificationApi';
import { cn } from '@/lib/utils';
import { BaseIconButton } from '@/components/uiKit/Button';
import { BaseButton } from '@/components/uiKit/Button';
import { motion } from 'framer-motion';
import { BaseDialog } from '@/components';
import { NotificationModalContent, NotificationModalTitle } from './NotificationModal';
import { TrashIcon } from '@/assets/Icons';
import { Icon as IconWrapper } from '@/components/shared/display/Icon';
import { colors, HealthSeverity } from '@/constants/colors';

interface NotificationsRowProps {
  notification: NotificationAlert;
}

export const NotificationRow = ({ notification }: NotificationsRowProps) => {
  const [isModalOpened, setIsModalOpened] = useState(false);

  const Icon = HEALTH_STATES[notification.severity].icon;

  const [clearNotifications] = useClearNotificationsMutation();
  const [readNotifications] = useReadNotificationsMutation();
  const [fetchNotifications] = useLazyGetNotificationsQuery();

  const [isRemoveClicked, setRemoveClicked] = useState(false);
  const [isDialogOpened, setDialogOpened] = useState(false);

  const handleModalOpenChange = (isOpened: boolean) => {
    setIsModalOpened(isOpened);
  };

  const onNotificationClear = async () => {
    const id = notification.notification_id;

    await clearNotifications([id]);
    setDialogOpened(false);

    await fetchNotifications({});
    setRemoveClicked(false);
  };

  const onNotificationClick = async () => {
    handleModalOpenChange(true);
    await readNotifications([notification.notification_id]);
    await fetchNotifications({});
  };

  const onCancel = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    setDialogOpened(false);
  };

  const onDeleteSubmit = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    setRemoveClicked(true);
    await onNotificationClear();
  };

  const onClearClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    setDialogOpened(true);
  };

  return (
    <>
      <motion.div
        initial={false}
        animate={{ opacity: !isRemoveClicked ? 1 : 0, height: !isRemoveClicked ? 'auto' : 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="overflow-hidden"
      >
        <div
          onClick={onNotificationClick}
          className={cn(
            'group px-6 hover:cursor-pointer hover:bg-[#f7f7f7] border-b border-[#E2E8F0]',
            !notification.read ? 'bg-[rgba(112, 113, 243, 0.06)]' : ''
          )}
        >
          {isDialogOpened ? (
            <div className="py-3 text-center">
              <div className="text-sm pb-2">Are you sure you want to delete?</div>
              <div className="flex justify-center gap-2">
                <BaseButton
                  size="xs"
                  variant="filled"
                  onClick={onCancel}
                  className="px-2 py-1 h-auto text-xs font-semibold text-inherit bg-gray-300 bg-opacity-30 hover:bg-opacity-70 hover:bg-gray-300 active:bg-opacity-100 active:bg-gray-300"
                >
                  Cancel
                </BaseButton>
                <BaseButton
                  size="xs"
                  variant="filled"
                  onClick={onDeleteSubmit}
                  className="px-2 py-1 h-auto text-stone-50 text-xs font-semibold bg-red-600 hover:bg-opacity-70 hover:bg-red-600 active:bg-opacity-100 active:bg-red-600"
                >
                  Delete
                </BaseButton>
              </div>
            </div>
          ) : (
            <div className="flex justify-between items-center gap-2">
              <div className="flex items-center gap-2">
                <div>
                  <div className="w-10 h-10 bg-[#EDF2F7] flex justify-center items-center rounded-full">
                    <Icon className={`text-[${colors.health[notification.severity as HealthSeverity]}] w-6 h-6`} />
                  </div>
                </div>
                <div className="text-xs py-5 flex flex-col gap-2">
                  <div className="font-semibold">
                    {notification.asset_ui_name}
                    {notification.component_name ? ` / ${notification.component_name}` : null} –{' '}
                    {notification.event_ui_name}
                  </div>
                  <div>
                    {notification.site_name} {format(new Date(notification.created_at), DATE_TIME_FORMAT)}
                  </div>
                </div>
              </div>
              <BaseIconButton
                icon={TrashIcon}
                onClick={onClearClick}
                variant="outline"
                classNames={{
                  icon: 'text-red-600 !w-3 !h-3',
                  button:
                    'min-h-5 min-w-5 ring-red-700 hover:bg-red-600 hover:bg-opacity-5 hover:ring-red-700 active:ring-1 group-hover:opacity-100 opacity-0',
                }}
              />
            </div>
          )}
        </div>
      </motion.div>
      <BaseDialog
        content={<NotificationModalContent notification={notification} />}
        title={<NotificationModalTitle notification={notification} />}
        open={isModalOpened}
        onOpenChange={handleModalOpenChange}
        className="max-w-[70vw]"
      />
    </>
  );
};
